/**
 * CORD-01 Private Streams: the three-layer wrap/seal/rumor envelope, plus the
 * standard NIP-59 giftwrap used for person-to-person delivery (Direct Invites
 * CORD-05 §6, and this app's join requests/responses).
 */

import { finalizeEvent, generateSecretKey, getEventHash, getPublicKey, verifyEvent } from "nostr-tools";
import * as nip44 from "nostr-tools/nip44";
import type { NostrEvent } from "@nostrify/nostrify";

import { KIND_SEAL_ENCRYPTED, KIND_SEAL_PLAINTEXT, KIND_WRAP } from "./config";
import type { DecodedStreamEvent, StreamKey } from "./types";

/** What a signer must provide for us to seal events. */
export interface SealSigner {
  signEvent(template: {
    kind: number;
    content: string;
    tags: string[][];
    created_at: number;
  }): Promise<NostrEvent>;
}

export interface RumorTemplate {
  kind: number;
  content: string;
  tags: string[][];
  created_at?: number;
}

function msOf(dateMs: number): string {
  return String(dateMs % 1000);
}

/** Current time split the Concord way: `created_at` seconds + `["ms"]` tag. */
export function concordTime(dateMs = Date.now()): { created_at: number; ms: string } {
  return { created_at: Math.floor(dateMs / 1000), ms: msOf(dateMs) };
}

/**
 * Build a stream event: rumor → seal (signed by the author's real key) →
 * kind 1059 wrap (signed by the plane's derived stream key, ephemeral `p`).
 *
 * Encrypted seal (kind 20013) for Chat/Guestbook planes, plaintext seal
 * (kind 20014) for the Control Plane (CORD-02 §5).
 */
export async function createStreamEvent(opts: {
  stream: StreamKey;
  sealKind: typeof KIND_SEAL_ENCRYPTED | typeof KIND_SEAL_PLAINTEXT;
  authorPub: string;
  signer: SealSigner;
  rumor: RumorTemplate;
  wrapKind?: number;
}): Promise<NostrEvent> {
  const { stream, sealKind, authorPub, signer, rumor } = opts;
  const created_at = rumor.created_at ?? Math.floor(Date.now() / 1000);

  const rumorUnsigned = {
    kind: rumor.kind,
    pubkey: authorPub,
    content: rumor.content,
    tags: rumor.tags,
    created_at,
  };
  const rumorWithId = { ...rumorUnsigned, id: getEventHash(rumorUnsigned) };
  const rumorJson = JSON.stringify(rumorWithId);

  const sealContent =
    sealKind === KIND_SEAL_ENCRYPTED
      ? nip44.v2.encrypt(rumorJson, stream.convKey)
      : rumorJson;

  const seal = await signer.signEvent({
    kind: sealKind,
    content: sealContent,
    tags: [],
    created_at,
  });

  const ephemeralPk = getPublicKey(generateSecretKey());
  const wrap = finalizeEvent(
    {
      kind: opts.wrapKind ?? KIND_WRAP,
      content: nip44.v2.encrypt(JSON.stringify(seal), stream.convKey),
      tags: [["p", ephemeralPk]],
      created_at,
    },
    stream.sk,
  );
  return wrap as NostrEvent;
}

/**
 * Open a stream event: decrypt the wrap with the plane's conversation key,
 * verify the seal, open the seal (decrypting when encrypted), and recompute
 * the rumor id (an embedded id is never trusted, CORD-04 §7).
 */
export function decodeStreamEvent(
  wrap: NostrEvent,
  stream: Pick<StreamKey, "pk" | "convKey">,
): DecodedStreamEvent | null {
  try {
    if (wrap.pubkey !== stream.pk) return null;
    const sealJson = nip44.v2.decrypt(wrap.content, stream.convKey);
    const seal = JSON.parse(sealJson) as NostrEvent;
    if (seal.kind !== KIND_SEAL_ENCRYPTED && seal.kind !== KIND_SEAL_PLAINTEXT) {
      return null;
    }
    if (!verifyEvent(seal)) return null;

    const rumorJson =
      seal.kind === KIND_SEAL_ENCRYPTED
        ? nip44.v2.decrypt(seal.content, stream.convKey)
        : seal.content;
    const rumor = JSON.parse(rumorJson) as {
      kind: number;
      pubkey: string;
      created_at: number;
      tags: string[][];
      content: string;
    };
    // NIP-59's impersonation check: the rumor's author must be the seal's.
    if (rumor.pubkey !== seal.pubkey) return null;
    const id = getEventHash({
      kind: rumor.kind,
      pubkey: rumor.pubkey,
      created_at: rumor.created_at,
      tags: rumor.tags,
      content: rumor.content,
    });
    return { wrap, seal, rumor: { ...rumor, id } };
  } catch {
    return null;
  }
}

/** The `["ms"]` sub-second tag; a value outside 0..999 is malformed (CORD-02 §5). */
export function rumorTimeMs(rumor: { created_at: number; tags: string[][] }): number | null {
  const msTag = rumor.tags.find(([n]) => n === "ms")?.[1];
  if (msTag === undefined) return rumor.created_at * 1000;
  const ms = Number(msTag);
  if (!Number.isInteger(ms) || ms < 0 || ms > 999) return null;
  return rumor.created_at * 1000 + ms;
}

// ---------------------------------------------------------------------------
// Standard NIP-59 giftwraps (person-to-person, CORD-05 §6 shape)
// ---------------------------------------------------------------------------

export interface GiftUnwrap {
  seal: NostrEvent;
  rumor: {
    id: string;
    kind: number;
    pubkey: string;
    created_at: number;
    tags: string[][];
    content: string;
  };
}

export interface GiftSigner extends SealSigner {
  nip44?: {
    encrypt(pubkey: string, plaintext: string): Promise<string>;
    decrypt(pubkey: string, ciphertext: string): Promise<string>;
  };
}

function tweakedTimestamp(): number {
  // NIP-59: randomize timestamps up to two days into the past.
  return Math.floor(Date.now() / 1000) - Math.floor(Math.random() * 172800);
}

/**
 * Create a standard NIP-59 giftwrap addressed to a person: an ephemeral-author
 * kind 1059 wrap around a kind 13 seal around the rumor. Used for Concord
 * Direct Invites (kind 3313) and this app's join requests/responses.
 */
export async function createGiftWrap(opts: {
  signer: GiftSigner;
  senderPub: string;
  recipientPub: string;
  rumorKind: number;
  content: string;
  tags?: string[][];
  /** Outer index hint, e.g. ["k", "3313"] (CORD-05 §6). */
  outerTags?: string[][];
}): Promise<NostrEvent> {
  const { signer, senderPub, recipientPub, rumorKind, content } = opts;
  if (!signer.nip44) throw new Error("Signer does not support NIP-44");

  const rumorUnsigned = {
    kind: rumorKind,
    pubkey: senderPub,
    content,
    tags: opts.tags ?? [],
    created_at: Math.floor(Date.now() / 1000),
  };
  const rumorWithId = { ...rumorUnsigned, id: getEventHash(rumorUnsigned) };

  const sealContent = await signer.nip44.encrypt(
    recipientPub,
    JSON.stringify(rumorWithId),
  );
  const seal = await signer.signEvent({
    kind: 13,
    content: sealContent,
    tags: [],
    created_at: tweakedTimestamp(),
  });

  const ephemeralSk = generateSecretKey();
  const ephemeralPk = getPublicKey(ephemeralSk);
  const wrapConv = nip44.v2.utils.getConversationKey(ephemeralSk, recipientPub);
  const wrap = finalizeEvent(
    {
      kind: KIND_WRAP,
      content: nip44.v2.encrypt(JSON.stringify(seal), wrapConv),
      tags: [["p", recipientPub], ...(opts.outerTags ?? [])],
      created_at: tweakedTimestamp(),
    },
    ephemeralSk,
  );
  return wrap as NostrEvent;
}

/** Open a standard NIP-59 giftwrap addressed to the current user. */
export async function unwrapGiftWrap(
  wrap: NostrEvent,
  signer: GiftSigner,
): Promise<GiftUnwrap | null> {
  try {
    if (!signer.nip44) return null;
    const sealJson = await signer.nip44.decrypt(wrap.pubkey, wrap.content);
    const seal = JSON.parse(sealJson) as NostrEvent;
    if (seal.kind !== 13) return null;
    if (!verifyEvent(seal)) return null;

    const rumorJson = await signer.nip44.decrypt(seal.pubkey, seal.content);
    const rumor = JSON.parse(rumorJson) as GiftUnwrap["rumor"];
    if (rumor.pubkey !== seal.pubkey) return null;
    const id = getEventHash({
      kind: rumor.kind,
      pubkey: rumor.pubkey,
      created_at: rumor.created_at,
      tags: rumor.tags,
      content: rumor.content,
    });
    return { seal, rumor: { ...rumor, id } };
  } catch {
    return null;
  }
}
